import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { Modal } from '../../components/common/Modal';
import type { Product, ProductFormData } from '../../types/product';
import { ProductFormContent } from './ProductFormContent';
import { api } from '../../services/api';

const initialFormData: ProductFormData = {
  name: '',
  description: '',
  price: 0,
  imageUrl: '',
  category: 'Apparel',
  type: 'clothing',
  stockQuantity: 0,
  metadata: {
    material: '',
    variants: [],
    availableColors: { mens: [], womens: [] },
    availableSizes: [],
    availableFits: ['mens']
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  isActive: true
};

export const ProductsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const { data: products = [], isLoading } = useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: async () => {
      const data = await api.admin.getProducts();
      return data.data;
    }
  });

  const toggleProductStatus = useMutation<Product, Error, { id: string, isActive: boolean }>({
    mutationFn: async ({ id, isActive }) => {
      const result = await api.admin.updateProduct(id, { isActive });
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product status updated successfully');
    },
    onError: (error) => {
      console.error('Error updating product status:', error);
      toast.error('Error updating product status');
    }
  });

  const toggleSelectedProduct = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const toggleAllProducts = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  const bulkToggleStatus = async (isActive: boolean) => {
    try {
      await Promise.all(
        Array.from(selectedProducts).map(id =>
          toggleProductStatus.mutateAsync({ id, isActive })
        )
      );
      setSelectedProducts(new Set());
      toast.success(`Successfully ${isActive ? 'activated' : 'deactivated'} selected products`);
    } catch (error) {
      toast.error('Error updating product statuses');
    }
  };

  const createProduct = useMutation<Product, Error, ProductFormData>({
    mutationFn: async (data) => {
      const result = await api.admin.createProduct(data);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product created successfully');
      setIsModalOpen(false);
      setEditingProduct(null);
    },
    onError: (error) => {
      console.error('Error creating product:', error);
      toast.error('Error creating product');
    }
  });

  const updateProduct = useMutation<Product, Error, Product>({
    mutationFn: async (data) => {
      const result = await api.admin.updateProduct(data.id, data);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product updated successfully');
      setIsModalOpen(false);
      setEditingProduct(null);
    },
    onError: (error) => {
      console.error('Error updating product:', error);
      toast.error('Error updating product');
    }
  });

  const deleteProduct = useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await api.admin.deleteProduct(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting product:', error);
      toast.error('Error deleting product');
    }
  });

  const handleSubmit = async (data: ProductFormData) => {
    if (editingProduct) {
      const updatedProduct = {
        ...editingProduct,
        name: data.name,
        description: data.description,
        price: data.price,
        imageUrl: data.imageUrl,
        category: data.category,
        type: data.type,
        stockQuantity: data.stockQuantity,
        metadata: data.metadata
      };
      await updateProduct.mutateAsync(updatedProduct);
    } else {
      await createProduct.mutateAsync(data);
    }
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="glass-panel">
        <div className="p-6 space-y-6">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <h1 className="text-2xl font-semibold text-teal-300">Products</h1>
            <div className="flex gap-2">
              {selectedProducts.size > 0 && (
                <>
                  <button
                    onClick={() => bulkToggleStatus(true)}
                    className="button-secondary"
                  >
                    Activate Selected
                  </button>
                  <button
                    onClick={() => bulkToggleStatus(false)}
                    className="button-secondary"
                  >
                    Deactivate Selected
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setIsModalOpen(true);
                }}
                className="button-primary flex items-center justify-center gap-2 min-w-[140px]"
              >
                <PlusIcon className="h-5 w-5" />
                Add Product
              </button>
            </div>
          </div>

          {/* Search Section */}
          <div className="relative max-w-md w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search products..."
              className="form-input pl-10 w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Products Table */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto"></div>
              <p className="mt-2 text-gray-400">Loading products...</p>
            </div>
          ) : filteredProducts?.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400">No products found</p>
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setIsModalOpen(true);
                }}
                className="button-secondary mt-4"
              >
                Add your first product
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-800">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-black/30">
                  <tr>
                    <th scope="col" className="table-header w-12">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          className="rounded border-gray-600 text-teal-500 focus:ring-teal-500"
                          checked={selectedProducts.size === products.length}
                          onChange={toggleAllProducts}
                        />
                      </div>
                    </th>
                    <th scope="col" className="table-header">Status</th>
                    <th scope="col" className="table-header">Name</th>
                    <th scope="col" className="table-header">Description</th>
                    <th scope="col" className="table-header">Price</th>
                    <th scope="col" className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800 bg-black/20">
                  {filteredProducts?.map((product) => (
                    <tr key={product.id} className="hover:bg-black/40 transition-colors">
                      <td className="table-cell">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-600 text-teal-500 focus:ring-teal-500"
                            checked={selectedProducts.has(product.id)}
                            onChange={() => toggleSelectedProduct(product.id)}
                          />
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-3 h-3 rounded-full ${
                              product.isActive ? 'bg-green-500' : 'bg-red-500'
                            }`}
                          />
                          <button
                            onClick={() => toggleProductStatus.mutate({
                              id: product.id,
                              isActive: !product.isActive
                            })}
                            className={`text-sm ${
                              product.isActive ? 'text-green-500' : 'text-red-500'
                            } hover:underline`}
                          >
                            {product.isActive ? 'Active' : 'Inactive'}
                          </button>
                        </div>
                      </td>
                      <td className="table-cell font-medium">{product.name}</td>
                      <td className="table-cell">{product.description}</td>
                      <td className="table-cell">${product.price.toFixed(2)}</td>
                      <td className="table-cell text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => {
                              openEditModal(product);
                              setIsModalOpen(true);
                            }}
                            className="button-secondary"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this product?')) {
                                deleteProduct.mutate(product.id);
                              }
                            }}
                            className="button-danger"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProduct(null);
        }}
        title={editingProduct ? 'Edit Product' : 'Add Product'}
      >
        <ProductFormContent
          onSubmit={handleSubmit}
          initialData={editingProduct}
          isSubmitting={createProduct.isPending || updateProduct.isPending}
        />
      </Modal>
    </div>
  );
};

export default ProductsPage; 